// src/pages/admin/AdminAbandonedCheckoutsPage.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FullScreenLoader } from "components/ui/Loader";
import { getErrorMessage } from "utils/errors";
import {
  ABANDONED_DEFAULTS,
  adminListAbandonedCheckouts,
  type AbandonedCheckout,
  type AbandonedCheckoutsOut,
} from "api/adminAbandonedCheckouts";

/**
 * Customers who opened checkout and did not pay, for following up by hand.
 * Read-only: it shows what the backend returns and changes nothing. The backend
 * decides who is listed (an unpaid order over an hour old, not trying again now,
 * and a marketing grant on record), and returns nothing about her health beyond
 * the test she chose, so this page shows nothing more.
 */
const ROUTE_LABEL: Record<"clinic" | "mobile", string> = {
  clinic: "Clinic",
  mobile: "Home visit",
};

const STATUS_LABEL: Record<AbandonedCheckout["status"], string> = {
  payment_pending: "Not paid",
  failed: "Payment failed",
};

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString();
}

const AdminAbandonedCheckoutsPage: React.FC = () => {
  const [data, setData] = useState<AbandonedCheckoutsOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Only the newest request may update the page, so a slow older answer cannot land last.
  const latest = useRef(0);

  const load = useCallback(async () => {
    const request = ++latest.current;
    setErr("");
    setLoading(true);
    try {
      const res = await adminListAbandonedCheckouts();
      if (request !== latest.current) return;
      setData(res);
    } catch (e: unknown) {
      if (request !== latest.current) return;
      // Never leave an older list up beside the error: someone on it may have paid or
      // withdrawn consent since, and it would still read as people to email.
      setData(null);
      setErr(getErrorMessage(e, "Failed to load abandoned checkouts"));
    } finally {
      if (request === latest.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <FullScreenLoader label="Loading…" />;

  const items = data?.items ?? [];

  return (
    <main className="relative min-h-screen px-4 py-12 sm:py-16">
      <header className="text-center">
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#ceddf2]">
          Admin · Abandoned checkouts
        </h1>
      </header>

      <section className="mt-16 sm:mt-20 flex justify-center">
        <div className="w-full bg-white rounded-2xl shadow-sm border border-slate-200 px-6 py-8 sm:px-8">
          <div className="flex items-start justify-between gap-4">
            <Link
              to="/admin"
              className="font-ui text-xs uppercase tracking-wide text-slate-500 hover:opacity-80"
            >
              ← Back to Admin Home
            </Link>
            <button
              type="button"
              onClick={() => void load()}
              className={[
                "inline-flex items-center justify-center rounded-full",
                "border border-slate-200 bg-white",
                "px-4 py-2",
                "text-xs tracking-[0.18em] uppercase text-[#151A3A]",
                "hover:bg-slate-50 transition-colors font-ui font-bold",
              ].join(" ")}
            >
              Refresh
            </button>
          </div>

          {data && (
            <p className="mt-6 font-body text-slate-600">
              Customers who opened checkout in the last {data.within_days} days and had not paid{" "}
              {data.left_after_minutes} minutes later, and who agreed to hear from us by email.
              Newest first, one row per customer.
            </p>
          )}

          {items.length >= ABANDONED_DEFAULTS.limit && (
            <p className="mt-3 font-body text-amber-800">
              Showing the newest {ABANDONED_DEFAULTS.limit}. There may be more.
            </p>
          )}

          {err && (
            <div className="mt-6 border border-red-300 bg-red-50 text-red-700 p-3 rounded">
              {err}
            </div>
          )}

          <div className="mt-8">
            {!err && items.length === 0 ? (
              <div className="font-body text-slate-600">No abandoned checkouts.</div>
            ) : items.length > 0 ? (
              <div className="overflow-x-auto rounded-2xl border border-slate-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr className="text-slate-700 font-ui text-xs uppercase tracking-wide">
                      <th className="text-left p-3">Customer</th>
                      <th className="text-left p-3">Email</th>
                      <th className="text-left p-3">Test</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-left p-3">Opened checkout</th>
                      <th className="text-left p-3">Order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row) => (
                      <tr key={row.user_id} className="border-t border-slate-200">
                        <td className="p-3 font-body text-slate-900 break-words">
                          {row.name || "-"}
                        </td>
                        <td className="p-3 font-body text-slate-700 break-words">
                          {row.email ? (
                            <a href={`mailto:${encodeURIComponent(row.email)}`} className="underline">
                              {row.email}
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="p-3 font-body text-slate-700">
                          {row.package_name || "-"}
                          {row.route ? ` · ${ROUTE_LABEL[row.route]}` : ""}
                        </td>
                        <td className="p-3 font-body text-slate-700">
                          {STATUS_LABEL[row.status] ?? row.status}
                        </td>
                        <td className="p-3 font-body text-slate-700">
                          {formatWhen(row.started_at)}
                        </td>
                        <td className="p-3">
                          <Link
                            to={`/admin/orders/${row.order_id}`}
                            className="font-body text-slate-900 underline"
                          >
                            #{row.order_id}
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </main>
  );
};

export default AdminAbandonedCheckoutsPage;
